import React, { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import dayjs from 'dayjs';
import { Select, SelectContent, SelectItem, SelectTrigger } from '@/components/ui/select';
import { FormFieldV2 } from '@/components/custom/erp/form-field-v2';
import { AritoIcon } from '@/components/custom/arito';
import { DATE_RANGE_OPTIONS } from '@/lib/dateUtil';
import { cn } from '@/lib/utils';

interface DateRangeDropdownV2Props {
  label?: string;
  fromDateName?: string;
  toDateName?: string;
  fromFieldClassName?: string;
  toFieldClassName?: string;
  setDates?: (fromDate: string, toDate: string) => void;
}

const DateRangeDropdownV2: React.FC<DateRangeDropdownV2Props> = ({
  fromDateName,
  toDateName,
  fromFieldClassName,
  toFieldClassName,
  setDates
}) => {
  const resolvedFromDateName = fromDateName || 'ngay_ct1';
  const resolvedToDateName = toDateName || 'ngay_ct2';
  const [open, setOpen] = useState(false);
  const { setValue, watch } = useFormContext();
  const fromValue = watch(resolvedFromDateName);
  const toValue = watch(resolvedToDateName);
  const storageKey = `${resolvedFromDateName}-${resolvedToDateName}-date-range`;
  const [savedRange, setSavedRange] = useState<{ from: string; to: string } | null>(null);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (!saved) return;

      const parsed = JSON.parse(saved);
      if (parsed?.from && parsed?.to) {
        setSavedRange(parsed);
      }
    } catch (error) {
      console.error('Failed to load saved date range', error);
    }
  }, [storageKey]);

  useEffect(() => {
    if (!savedRange || fromValue || toValue) return;

    setValue(resolvedFromDateName, savedRange.from, { shouldDirty: false });
    setValue(resolvedToDateName, savedRange.to, { shouldDirty: false });
    setDates?.(savedRange.from, savedRange.to);
  }, [fromValue, resolvedFromDateName, resolvedToDateName, savedRange, setDates, setValue, toValue]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      try {
        if (!fromValue || !toValue) return;

        localStorage.setItem(
          storageKey,
          JSON.stringify({
            from: dayjs(fromValue).format('YYYY-MM-DD'),
            to: dayjs(toValue).format('YYYY-MM-DD')
          })
        );
      } catch (error) {
        console.error('Failed to save date range', error);
      }
    }, 300);

    return () => clearTimeout(timeout);
  }, [fromValue, storageKey, toValue]);

  const handleSelectOption = (optionId: string) => {
    const option = DATE_RANGE_OPTIONS.find(item => item.id === optionId);
    if (!option) return;

    const { from, to } = option.getValue(dayjs());
    setValue(resolvedFromDateName, from, { shouldDirty: true });
    setValue(resolvedToDateName, to, { shouldDirty: true });
    setDates?.(from, to);
  };

  return (
    <div className='flex w-full items-center gap-3'>
      <div className={cn('min-w-0 flex-1', fromFieldClassName && 'flex-none', fromFieldClassName)}>
        <FormFieldV2 name={resolvedFromDateName} type='date' />
      </div>

      <div className={cn('min-w-0 flex-1', toFieldClassName && 'flex-none', toFieldClassName)}>
        <FormFieldV2 name={resolvedToDateName} type='date' />
      </div>

      <Select open={open} onOpenChange={setOpen} onValueChange={handleSelectOption}>
        <SelectTrigger
          className='flex h-8 w-8 items-center justify-center rounded border border-gray-300 bg-white p-0 text-gray-600 shadow-none hover:bg-gray-50 hover:text-gray-700 [&>svg:last-child]:hidden'
          aria-label='Chon nhanh khoang thoi gian'
          onMouseEnter={() => setOpen(true)}
        >
          <AritoIcon icon={284} />
        </SelectTrigger>
        <SelectContent align='end' className='z-[9999] w-56'>
          {DATE_RANGE_OPTIONS.map(option => (
            <SelectItem key={option.id} value={option.id}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

export default DateRangeDropdownV2;
